import type { AdminCardDto } from '@carcheck/shared';
import type { GridPaginationModel } from '@mui/x-data-grid';
import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ForceUnbindDialog } from './ForceUnbindDialog';
import { AdminDataGrid } from '../../../components/data-grid';
import { buildCardColumns } from '../_lib/cardColumns';
import { useForceUnbind } from '../_lib/useForceUnbind';

/**
 * Shared cards DataGrid + force-unbind orchestration (AUT-126). The SINGLE
 * place that owns the force-unbind dialog state, the `useForceUnbind` hook,
 * and the status-gated columns — reused verbatim by the standalone `/cards`
 * page AND the inspector-detail Cards tab (no duplication).
 *
 * Action eligibility is derived from the FRESHEST `rows` slice, never a
 * frozen snapshot (mirrors `PayoutsListPage`'s `selected = items.find(...)`):
 * the confirm dialog opens by id, and the card it acts on is re-found in the
 * current `rows` on every render. A post-success refetch hands fresh rows
 * (the unbound card is no longer `active`), so its action button disappears
 * and the dialog — if somehow still keyed to it — fail-closes (the derived
 * card is gone → the confirm is a no-op against a missing row). On a 409
 * rejection the hook invalidates → the same re-sync runs.
 *
 * The grid never sends a sort (backend is fixed `createdAt DESC`); pagination
 * is owned by the parent (`useCursorPagination`) and threaded through.
 */
export interface CardsGridProps {
  rows: AdminCardDto[];
  rowCount: number;
  loading: boolean;
  error: Error | null;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  emptyState: ReactNode;
}

export const CardsGrid = ({
  rows,
  rowCount,
  loading,
  error,
  paginationModel,
  onPaginationModelChange,
  emptyState,
}: CardsGridProps): ReactElement => {
  const { t } = useTranslation();
  const forceUnbind = useForceUnbind();
  const [pendingId, setPendingId] = useState<string | null>(null);

  // Derive the card-to-unbind from the FRESHEST slice — never a frozen
  // snapshot. `null` once the row leaves the slice (refetch / filter moved
  // it out) → the confirm fail-closes.
  const pendingCard = useMemo(
    () => rows.find((r) => r.id === pendingId) ?? null,
    [rows, pendingId],
  );

  const columns = useMemo(
    () => buildCardColumns({ t, busy: forceUnbind.busy, onForceUnbind: setPendingId }),
    [t, forceUnbind.busy],
  );

  const onConfirm = (): void => {
    if (!pendingCard) {
      setPendingId(null);
      return;
    }
    void forceUnbind.run(pendingCard.id).then((ok) => {
      if (ok) setPendingId(null);
    });
  };

  return (
    <>
      <AdminDataGrid<AdminCardDto>
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        error={error}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={[]}
        onSortModelChange={() => undefined}
        emptyState={emptyState}
      />

      <ForceUnbindDialog
        open={pendingCard !== null}
        last4={pendingCard?.last4 ?? ''}
        loading={forceUnbind.busy}
        onConfirm={onConfirm}
        onCancel={() => setPendingId(null)}
      />
    </>
  );
};
